import React, { Component, ReactNode } from 'react';
import { Box, Alert } from '@amzn/awsui-components-console';

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree and displays
 * a fallback UI instead of crashing the entire application.
 *
 * Following SCS Console pattern for error handling:
 * - Catches errors in child components
 * - Displays user-friendly error messages
 * - Logs errors to console for debugging
 *
 * Requirements:
 * - 7.5: Follow feature-based folder structure pattern from SCS Console
 * - Error boundary implementation for fault tolerance
 */

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Update state when an error is caught
   * This lifecycle method is called after an error has been thrown by a descendant component
   */
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /**
   * Log error details to console for debugging
   * This lifecycle method is called after an error has been thrown by a descendant component
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box padding="xl">
          <Alert
            type="error"
            header="Application Error"
            action={
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  backgroundColor: '#0972d3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                }}
              >
                Reload Page
              </button>
            }
          >
            Something went wrong while rendering this page. Please try reloading the page.
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}
